import Clarity from "@microsoft/clarity";
import { useJsApiLoader } from "@react-google-maps/api";
import { PrimeReactProvider } from "primereact/api";
import { useEffect } from "react";
import { Route, BrowserRouter as Router, Routes, useLocation } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { NavProvider } from "./contexts/NavContext";

import CssBaseline from "@mui/material/CssBaseline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/lara-light-blue/theme.css";
import Styles from "./styles/_global/Styles";

import ForgotPassword from "./components/Auth/ForgotPassword";
import PasswordReset from "./components/Auth/PasswordReset";
import RequireAuth from "./components/Auth/RequireAuth";
import SignIn from "./components/Auth/SignIn";
import Home from "./components/Pages/Index";
import OnBoarding from "./components/Pages/Onboarding";

const theme = createTheme({
  palette: {
    primary: {
      main: Styles.primary.main,
    },
    secondary: {
      main: Styles.secondary.main,
    },
  },
});

// Hybrid tracking: GA4 for pages, GTM for events
function useAnalyticsTracking() {
  const location = useLocation();

  useEffect(() => {
    const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    const GTM_ID = process.env.REACT_APP_GTM_ID;
    
    // Initialize GTM (for custom events only)
    if (GTM_ID && !window.dataLayer) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({'gtm.start': new Date().getTime(), event: 'gtm.js'});
      
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
      document.head.appendChild(gtmScript);
      
      console.log('✅ GTM initialized:', GTM_ID);
    }
    
    // GA4 handled via GTM only
  }, []);

  // Track page views via GTM dataLayer
  useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'page_view',
        page_path: location.pathname + location.search,
        page_title: document.title
      });
      console.log('📊 GTM page_view:', location.pathname + location.search);
    }
  }, [location]);
}

// Component wrapper for analytics tracker
function AnalyticsTracker() {
  useAnalyticsTracking();
  return null;
}

function App() {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    libraries: ["places"],
  });

  // Microsoft Clarity effect (init once, all modes, guard Strict Mode)
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!window.__clarityInitialized) {
      const projectId = process.env.REACT_APP_MICROSOFT_CLARITY_PROJECT_ID;
      if (projectId && projectId.trim().length > 0) {
        Clarity.init(projectId);
        window.__clarityInitialized = true;
        console.log("✅ Microsoft Clarity initialised:", projectId);
      } else {
        console.warn("No Microsoft Clarity Project ID found in env vars");
      }
    }
  }, []);

  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem("gmaps_loaded", "true");
    } else {
      localStorage.removeItem("gmaps_loaded");
    }
  }, [isLoaded]);

  if (loadError) {
    return <div>Google Maps failed to load.</div>;
  }

  if (!isLoaded) {
    return (
      <div style={{ textAlign: "center", marginTop: "20vh" }}>Loading…</div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <PrimeReactProvider>
        <AuthProvider>
          <NavProvider>
            <Router>
              {/* Analytics tracker */}
              <AnalyticsTracker />
              <Routes>
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <Home isMapsReady={isLoaded} />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <RequireAuth>
                      <Home isMapsReady={isLoaded} />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/credit-profile"
                  element={
                    <RequireAuth>
                      <Home path="credit-profile" isMapsReady={isLoaded} />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <RequireAuth>
                      <Home path="offers" isMapsReady={isLoaded} />
                    </RequireAuth>
                  }
                />
                <Route path="/register" element={<OnBoarding />} />
                <Route path="/signin" element={<SignIn />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<PasswordReset />} />
                <Route path="/onboarding" element={<OnBoarding />} />
              </Routes>
            </Router>
          </NavProvider>
        </AuthProvider>
      </PrimeReactProvider>
    </ThemeProvider>
  );
}

export default App;
